import { Fragment, useEffect, useId } from 'react';
import { warsHref, type HomeNotice } from '../../app/gameRoute';
import { warsPrefetch } from '../../app/lazyApps';
import wordmarkLarge from '../shell/art/ui_wordmark_large.png';
import survivalArt from './art/home_survival.webp';
import warsArt from './art/home_wars.webp';
import { HOME_TEXT } from './content';
import './home.css';

/** The card art's pixel size (2:1, about 2× the card's CSS width; `scripts/captureHomeArt.mjs`). */
const ART_WIDTH = 960;
const ART_HEIGHT = 480;

/**
 * The front page (ADR-040): a light mode picker that loads neither game. The SLIMEZ pixel
 * wordmark on the dark pixel backdrop, a one-line tagline and two cards of exactly the same size:
 *
 * - **Slime Wars** — playable: the whole card is ONE link to the Slime Wars entry (`?game=wars`, a
 *   full navigation, so refresh / back work).
 * - **Survival** (the Preserve) — "Coming soon": greyed out by CSS (its art stays in colour on
 *   disk, so it can light up later), not a link, `aria-disabled`, a disabled button. Survival
 *   stays reachable only by its hidden link `?game=preserve`.
 *
 * Each card shows a framed in-game scene of its mode. `notice`: a bare `?room=` that is not a
 * Slime Wars code (an old Survival share link) says Survival is not open yet.
 *
 * W032: the Slime Wars app chunk is prefetched once the page is idle after its first paint (not
 * under Save-Data) and when the card is pointed at or focused, so "Play" opens near-instantly.
 */
export function HomeScreen({ notice = null }: { readonly notice?: HomeNotice | null }) {
  useEffect(() => warsPrefetch.whenIdle(), []);
  const ids = {
    warsTitle: useId(),
    warsPitch: useId(),
    warsFacts: useId(),
  };
  const { wars, survival } = HOME_TEXT;
  return (
    <main className="home" data-testid="home">
      <div className="home__inner">
        <header className="home__head">
          <h1 className="home-wordmark">
            <img
              className="home-wordmark__art"
              src={wordmarkLarge}
              width={416}
              height={128}
              alt={HOME_TEXT.title}
              draggable={false}
            />
          </h1>
          <p className="home__tagline">{HOME_TEXT.tagline}</p>
        </header>

        {notice !== null && (
          <p className="home-notice" role="status" data-testid="home-notice">
            {HOME_TEXT.notice[notice]}
          </p>
        )}

        <section className="home-modes" aria-label={HOME_TEXT.modesLabel}>
          <a
            className="mode-card mode-card--wars"
            href={warsHref()}
            aria-labelledby={ids.warsTitle}
            aria-describedby={`${ids.warsPitch} ${ids.warsFacts}`}
            data-testid="home-card-wars"
            onPointerEnter={warsPrefetch.prefetch}
            onFocus={warsPrefetch.prefetch}
          >
            <div className="mode-card__frame">
              <img
                className="mode-card__art"
                src={warsArt}
                width={ART_WIDTH}
                height={ART_HEIGHT}
                alt=""
                decoding="async"
                draggable={false}
              />
            </div>
            <div className="mode-card__body">
              <div className="mode-card__head">
                <h2 className="mode-card__title" id={ids.warsTitle}>
                  {wars.title}
                </h2>
                <span className="mode-card__badge mode-card__badge--new">{wars.badge}</span>
              </div>
              <p className="mode-card__pitch" id={ids.warsPitch}>
                {wars.pitch}
              </p>
              <Facts id={ids.warsFacts} facts={wars.facts} />
              <span className="mode-card__cta mode-card__cta--play" aria-hidden="true">
                {wars.cta}
                <span className="mode-card__arrow">→</span>
              </span>
            </div>
          </a>

          <div
            className="mode-card mode-card--soon"
            role="group"
            aria-label={survival.label}
            aria-disabled="true"
            data-testid="home-card-survival"
          >
            <div className="mode-card__frame">
              <img
                className="mode-card__art"
                src={survivalArt}
                width={ART_WIDTH}
                height={ART_HEIGHT}
                alt=""
                decoding="async"
                draggable={false}
              />
            </div>
            <div className="mode-card__body">
              <div className="mode-card__head">
                <h2 className="mode-card__title">{survival.title}</h2>
                <span className="mode-card__subtitle">{survival.subtitle}</span>
                <span className="mode-card__badge mode-card__badge--soon">{survival.badge}</span>
              </div>
              <p className="mode-card__pitch">{survival.pitch}</p>
              <Facts facts={survival.facts} />
              <button
                type="button"
                className="mode-card__cta mode-card__cta--soon"
                disabled
                data-testid="home-survival-button"
              >
                {survival.cta}
              </button>
            </div>
          </div>
        </section>

        <footer className="home__foot">
          <p>{HOME_TEXT.footer}</p>
        </footer>
      </div>
    </main>
  );
}

/**
 * One line of facts, separated by middle dots (decoration only). A narrow card wraps at a dot,
 * never inside a fact.
 */
function Facts({ id, facts }: { readonly id?: string; readonly facts: readonly string[] }) {
  return (
    <p className="mode-card__facts" id={id}>
      {facts.map((fact, index) => (
        <Fragment key={fact}>
          {index > 0 && (
            <span className="mode-card__dot" aria-hidden="true">
              {' · '}
            </span>
          )}
          <span className="mode-card__fact">{fact}</span>
        </Fragment>
      ))}
    </p>
  );
}
